import { useRouterState } from '@tanstack/react-router';

/**
 * Pure predicate — is `path` active for the given `pathname`?
 *
 * Matching rules:
 * - An empty `path` never matches. (Callers may pass `''` as a no-op slot; an
 *   empty string is a prefix of every pathname, so matching it would light up
 *   unrelated items.)
 * - `'/'` matches only the exact root pathname.
 * - Any other `path` matches when the pathname equals it OR is a descendant
 *   route — `pathname` starts with `` `${path}/` ``. The trailing-slash boundary
 *   prevents false positives such as `/vehicles` matching `/vehicles-archive`.
 *
 * This stays a *prefix* match by design so a parent remains active on its detail
 * routes (e.g. `/settings/territories` on `/settings/territories/42`). When two
 * sibling nav paths legitimately nest (e.g. `/vehicles` and `/vehicles/add`),
 * picking the single most-specific match is the caller's responsibility — see
 * `useActiveChildPath` in `sidebar-nav.tsx`.
 */
export function isPathActive(pathname: string, path: string): boolean {
  if (!path) return false;
  if (path === '/') return pathname === '/';
  return pathname === path || pathname.startsWith(`${path}/`);
}

/**
 * Returns `true` when the current pathname matches the given path — exactly or
 * as a descendant route. See {@link isPathActive} for the matching rules.
 *
 * @param path - The path to match against (e.g. `'/tenants'`).
 */
export function useActivePath(path: string): boolean {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return isPathActive(pathname, path);
}
